/**
 * Date Utility Functions
 * 
 * Centralized date operations using date-fns instead of moment.js
 * This wrapper provides a consistent API for date formatting and manipulation
 * across the application while enabling tree-shaking and reducing bundle size.
 * 
 * @module utils/date
 */

import {
  format,
  parseISO,
  parse,
  isValid,
  addDays,
  addMonths,
  addYears,
  subDays,
  subMonths,
  subYears,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInSeconds,
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  isBefore,
  isAfter,
  isEqual,
  isSameDay,
  isSameMonth,
  isSameYear,
  isToday,
  isYesterday,
  isTomorrow,
  isWeekend,
  getDay,
  getMonth,
  getYear,
  setDay,
  setMonth,
  setYear,
} from 'date-fns';

/**
 * Format a date to a string
 * @param {Date|string|number} date - Date to format
 * @param {string} formatString - Format string (e.g., 'yyyy-MM-dd', 'dd/MM/yyyy HH:mm')
 * @returns {string} Formatted date string
 * 
 * @example
 * formatDate(new Date(), 'yyyy-MM-dd') // '2025-11-12'
 * formatDate('2025-11-12', 'dd/MM/yyyy') // '12/11/2025'
 */
export const formatDate = (date, formatString = 'yyyy-MM-dd') => {
  if (!date) return '';
  const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
  if (!isValid(dateObj)) return '';
  return format(dateObj, formatString);
};

/**
 * Parse a date string to a Date object
 * @param {string} dateString - Date string to parse
 * @param {string} formatString - Format of the input string
 * @returns {Date|null} Parsed date or null if invalid
 * 
 * @example
 * parseDate('2025-11-12', 'yyyy-MM-dd')
 * parseDate('12/11/2025', 'dd/MM/yyyy')
 */
export const parseDate = (dateString, formatString = 'yyyy-MM-dd') => {
  if (!dateString) return null;
  const parsed = parse(dateString, formatString, new Date());
  return isValid(parsed) ? parsed : null;
};

/**
 * Parse an ISO date string to a Date object
 * @param {string} isoString - ISO date string
 * @returns {Date|null} Parsed date or null if invalid
 * 
 * @example
 * parseISODate('2025-11-12T10:30:00Z')
 */
export const parseISODate = (isoString) => {
  if (!isoString) return null;
  const parsed = parseISO(isoString);
  return isValid(parsed) ? parsed : null;
};

/**
 * Check if a date is valid
 * @param {Date|string|number} date - Date to check
 * @returns {boolean} True if valid
 */
export const isValidDate = (date) => {
  if (!date) return false;
  const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
  return isValid(dateObj);
};

/**
 * Add days to a date
 * @param {Date|string} date - Base date
 * @param {number} amount - Number of days to add
 * @returns {Date} New date
 */
export const addDaysToDate = (date, amount) => {
  const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
  return addDays(dateObj, amount);
};

/**
 * Subtract days from a date
 * @param {Date|string} date - Base date
 * @param {number} amount - Number of days to subtract
 * @returns {Date} New date
 */
export const subtractDays = (date, amount) => {
  const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
  return subDays(dateObj, amount);
};

/**
 * Add months to a date
 * @param {Date|string} date - Base date
 * @param {number} amount - Number of months to add
 * @returns {Date} New date
 */
export const addMonthsToDate = (date, amount) => {
  const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
  return addMonths(dateObj, amount);
};

/**
 * Subtract months from a date
 * @param {Date|string} date - Base date
 * @param {number} amount - Number of months to subtract
 * @returns {Date} New date
 */
export const subtractMonths = (date, amount) => {
  const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
  return subMonths(dateObj, amount);
};

/**
 * Add years to a date
 * @param {Date|string} date - Base date
 * @param {number} amount - Number of years to add
 * @returns {Date} New date
 */
export const addYearsToDate = (date, amount) => {
  const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
  return addYears(dateObj, amount);
};

/**
 * Subtract years from a date
 * @param {Date|string} date - Base date
 * @param {number} amount - Number of years to subtract
 * @returns {Date} New date
 */
export const subtractYears = (date, amount) => {
  const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
  return subYears(dateObj, amount);
};

/**
 * Get difference in days between two dates
 * @param {Date|string} dateLeft - Later date
 * @param {Date|string} dateRight - Earlier date
 * @returns {number} Number of days
 */
export const daysBetween = (dateLeft, dateRight) => {
  const left = typeof dateLeft === 'string' ? parseISO(dateLeft) : new Date(dateLeft);
  const right = typeof dateRight === 'string' ? parseISO(dateRight) : new Date(dateRight);
  return differenceInDays(left, right);
};

/**
 * Get difference in hours between two dates
 * @param {Date|string} dateLeft - Later date
 * @param {Date|string} dateRight - Earlier date
 * @returns {number} Number of hours
 */
export const hoursBetween = (dateLeft, dateRight) => {
  const left = typeof dateLeft === 'string' ? parseISO(dateLeft) : new Date(dateLeft);
  const right = typeof dateRight === 'string' ? parseISO(dateRight) : new Date(dateRight);
  return differenceInHours(left, right);
};

/**
 * Get difference in minutes between two dates
 * @param {Date|string} dateLeft - Later date
 * @param {Date|string} dateRight - Earlier date
 * @returns {number} Number of minutes
 */
export const minutesBetween = (dateLeft, dateRight) => {
  const left = typeof dateLeft === 'string' ? parseISO(dateLeft) : new Date(dateLeft);
  const right = typeof dateRight === 'string' ? parseISO(dateRight) : new Date(dateRight);
  return differenceInMinutes(left, right);
};

/**
 * Get difference in seconds between two dates
 * @param {Date|string} dateLeft - Later date
 * @param {Date|string} dateRight - Earlier date
 * @returns {number} Number of seconds
 */
export const secondsBetween = (dateLeft, dateRight) => {
  const left = typeof dateLeft === 'string' ? parseISO(dateLeft) : new Date(dateLeft);
  const right = typeof dateRight === 'string' ? parseISO(dateRight) : new Date(dateRight);
  return differenceInSeconds(left, right);
};

/**
 * Get start of day
 * @param {Date|string} date - Date
 * @returns {Date} Start of day
 */
export const getStartOfDay = (date) => {
  const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
  return startOfDay(dateObj);
};

/**
 * Get end of day
 * @param {Date|string} date - Date
 * @returns {Date} End of day
 */
export const getEndOfDay = (date) => {
  const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
  return endOfDay(dateObj);
};

/**
 * Get start of week
 * @param {Date|string} date - Date
 * @returns {Date} Start of week
 */
export const getStartOfWeek = (date) => {
  const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
  return startOfWeek(dateObj);
};

/**
 * Get end of week
 * @param {Date|string} date - Date
 * @returns {Date} End of week
 */
export const getEndOfWeek = (date) => {
  const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
  return endOfWeek(dateObj);
};

/**
 * Get start of month
 * @param {Date|string} date - Date
 * @returns {Date} Start of month
 */
export const getStartOfMonth = (date) => {
  const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
  return startOfMonth(dateObj);
};

/**
 * Get end of month
 * @param {Date|string} date - Date
 * @returns {Date} End of month
 */
export const getEndOfMonth = (date) => {
  const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
  return endOfMonth(dateObj);
};

/**
 * Get start of year
 * @param {Date|string} date - Date
 * @returns {Date} Start of year
 */
export const getStartOfYear = (date) => {
  const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
  return startOfYear(dateObj);
};

/**
 * Get end of year
 * @param {Date|string} date - Date
 * @returns {Date} End of year
 */
export const getEndOfYear = (date) => {
  const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
  return endOfYear(dateObj);
};

/**
 * Check if first date is before second date
 * @param {Date|string} date - First date
 * @param {Date|string} dateToCompare - Second date
 * @returns {boolean} True if first is before second
 */
export const isBeforeDate = (date, dateToCompare) => {
  const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
  const compareObj = typeof dateToCompare === 'string' ? parseISO(dateToCompare) : new Date(dateToCompare);
  return isBefore(dateObj, compareObj);
};

/**
 * Check if first date is after second date
 * @param {Date|string} date - First date
 * @param {Date|string} dateToCompare - Second date
 * @returns {boolean} True if first is after second
 */
export const isAfterDate = (date, dateToCompare) => {
  const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
  const compareObj = typeof dateToCompare === 'string' ? parseISO(dateToCompare) : new Date(dateToCompare);
  return isAfter(dateObj, compareObj);
};

/**
 * Check if two dates are equal
 * @param {Date|string} date - First date
 * @param {Date|string} dateToCompare - Second date
 * @returns {boolean} True if equal
 */
export const isEqualDate = (date, dateToCompare) => {
  const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
  const compareObj = typeof dateToCompare === 'string' ? parseISO(dateToCompare) : new Date(dateToCompare);
  return isEqual(dateObj, compareObj);
};

/**
 * Check if two dates are on the same day
 * @param {Date|string} date - First date
 * @param {Date|string} dateToCompare - Second date
 * @returns {boolean} True if same day
 */
export const isSameDayAs = (date, dateToCompare) => {
  const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
  const compareObj = typeof dateToCompare === 'string' ? parseISO(dateToCompare) : new Date(dateToCompare);
  return isSameDay(dateObj, compareObj);
};

/**
 * Check if two dates are in the same month
 * @param {Date|string} date - First date
 * @param {Date|string} dateToCompare - Second date
 * @returns {boolean} True if same month
 */
export const isSameMonthAs = (date, dateToCompare) => {
  const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
  const compareObj = typeof dateToCompare === 'string' ? parseISO(dateToCompare) : new Date(dateToCompare);
  return isSameMonth(dateObj, compareObj);
};

/**
 * Check if two dates are in the same year
 * @param {Date|string} date - First date
 * @param {Date|string} dateToCompare - Second date
 * @returns {boolean} True if same year
 */
export const isSameYearAs = (date, dateToCompare) => {
  const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
  const compareObj = typeof dateToCompare === 'string' ? parseISO(dateToCompare) : new Date(dateToCompare);
  return isSameYear(dateObj, compareObj);
};

/**
 * Check if date is today
 * @param {Date|string} date - Date to check
 * @returns {boolean} True if today
 */
export const isTodayDate = (date) => {
  const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
  return isToday(dateObj);
};

/**
 * Check if date is yesterday
 * @param {Date|string} date - Date to check
 * @returns {boolean} True if yesterday
 */
export const isYesterdayDate = (date) => {
  const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
  return isYesterday(dateObj);
};

/**
 * Check if date is tomorrow
 * @param {Date|string} date - Date to check
 * @returns {boolean} True if tomorrow
 */
export const isTomorrowDate = (date) => {
  const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
  return isTomorrow(dateObj);
};

/**
 * Check if date is a weekend
 * @param {Date|string} date - Date to check
 * @returns {boolean} True if weekend
 */
export const isWeekendDate = (date) => {
  const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
  return isWeekend(dateObj);
};

/**
 * Get day of week (0-6, Sunday is 0)
 * @param {Date|string} date - Date
 * @returns {number} Day of week
 */
export const getDayOfWeek = (date) => {
  const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
  return getDay(dateObj);
};

/**
 * Get month (0-11, January is 0)
 * @param {Date|string} date - Date
 * @returns {number} Month
 */
export const getMonthNumber = (date) => {
  const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
  return getMonth(dateObj);
};

/**
 * Get year
 * @param {Date|string} date - Date
 * @returns {number} Year
 */
export const getYearNumber = (date) => {
  const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
  return getYear(dateObj);
};

/**
 * Set day of week
 * @param {Date|string} date - Date
 * @param {number} day - Day of week (0-6)
 * @returns {Date} New date
 */
export const setDayOfWeek = (date, day) => {
  const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
  return setDay(dateObj, day);
};

/**
 * Set month
 * @param {Date|string} date - Date
 * @param {number} month - Month (0-11)
 * @returns {Date} New date
 */
export const setMonthNumber = (date, month) => {
  const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
  return setMonth(dateObj, month);
};

/**
 * Set year
 * @param {Date|string} date - Date
 * @param {number} year - Year
 * @returns {Date} New date
 */
export const setYearNumber = (date, year) => {
  const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
  return setYear(dateObj, year);
};

/**
 * Supported date formats for invoices and documents
 * Inspired by easy-invoice-pdf date format options
 */
export const INVOICE_DATE_FORMATS = {
  ISO: { value: 'yyyy-MM-dd', label: 'ISO (2025-01-25)', example: '2025-01-25' },
  EU: { value: 'dd/MM/yyyy', label: 'EU (25/01/2025)', example: '25/01/2025' },
  EU_DOT: { value: 'dd.MM.yyyy', label: 'EU Dot (25.01.2025)', example: '25.01.2025' },
  US: { value: 'MM/dd/yyyy', label: 'US (01/25/2025)', example: '01/25/2025' },
  UK: { value: 'dd-MM-yyyy', label: 'UK (25-01-2025)', example: '25-01-2025' },
  LONG_EU: { value: 'dd MMMM yyyy', label: 'Long EU (25 January 2025)', example: '25 January 2025' },
  LONG_US: { value: 'MMMM dd, yyyy', label: 'Long US (January 25, 2025)', example: 'January 25, 2025' },
  SHORT: { value: 'dd MMM yyyy', label: 'Short (25 Jan 2025)', example: '25 Jan 2025' },
};

/**
 * Get list of available date formats for selection
 * @returns {Array} Array of date format options
 */
export const getDateFormatOptions = () => {
  return Object.entries(INVOICE_DATE_FORMATS).map(([key, format]) => ({
    key,
    value: format.value,
    label: format.label,
    example: format.example,
  }));
};

/**
 * Format date for invoice display
 * @param {Date|string|number} date - Date to format
 * @param {string} formatKey - Format key from INVOICE_DATE_FORMATS (default: 'EU')
 * @returns {string} Formatted date string
 * 
 * @example
 * formatInvoiceDate(new Date(), 'EU') // '25/01/2025'
 * formatInvoiceDate(new Date(), 'US') // '01/25/2025'
 * formatInvoiceDate(new Date(), 'ISO') // '2025-01-25'
 */
export const formatInvoiceDate = (date, formatKey = 'EU') => {
  if (!date) return '';
  const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
  if (!isValid(dateObj)) return '';
  
  const formatConfig = INVOICE_DATE_FORMATS[formatKey] || INVOICE_DATE_FORMATS.EU;
  return format(dateObj, formatConfig.value);
};

/**
 * Format date with time for invoice timestamps
 * @param {Date|string|number} date - Date to format
 * @param {string} formatKey - Format key from INVOICE_DATE_FORMATS (default: 'EU')
 * @param {boolean} includeSeconds - Include seconds in time (default: false)
 * @returns {string} Formatted date and time string
 * 
 * @example
 * formatInvoiceDateTime(new Date(), 'EU') // '25/01/2025 14:30'
 * formatInvoiceDateTime(new Date(), 'US', true) // '01/25/2025 2:30:45 PM'
 */
export const formatInvoiceDateTime = (date, formatKey = 'EU', includeSeconds = false) => {
  if (!date) return '';
  const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
  if (!isValid(dateObj)) return '';
  
  const formatConfig = INVOICE_DATE_FORMATS[formatKey] || INVOICE_DATE_FORMATS.EU;
  const timeFormat = includeSeconds ? 'HH:mm:ss' : 'HH:mm';
  return format(dateObj, `${formatConfig.value} ${timeFormat}`);
};

/**
 * Calculate due date from invoice date
 * @param {Date|string} invoiceDate - Invoice date
 * @param {number} paymentTermsDays - Payment terms in days (default: 30)
 * @returns {Date} Due date
 * 
 * @example
 * calculateDueDate('2025-01-25', 30) // Date object for 2025-02-24
 */
export const calculateDueDate = (invoiceDate, paymentTermsDays = 30) => {
  const dateObj = typeof invoiceDate === 'string' ? parseISO(invoiceDate) : new Date(invoiceDate);
  if (!isValid(dateObj)) return null;
  return addDays(dateObj, paymentTermsDays);
};

/**
 * Check if invoice is overdue
 * @param {Date|string} dueDate - Due date
 * @returns {boolean} True if overdue
 */
export const isInvoiceOverdue = (dueDate) => {
  if (!dueDate) return false;
  const dueDateObj = typeof dueDate === 'string' ? parseISO(dueDate) : new Date(dueDate);
  if (!isValid(dueDateObj)) return false;
  return isBefore(dueDateObj, startOfDay(new Date()));
};

/**
 * Get days until due date (negative if overdue)
 * @param {Date|string} dueDate - Due date
 * @returns {number} Days until due (negative if overdue)
 */
export const getDaysUntilDue = (dueDate) => {
  if (!dueDate) return 0;
  const dueDateObj = typeof dueDate === 'string' ? parseISO(dueDate) : new Date(dueDate);
  if (!isValid(dueDateObj)) return 0;
  return differenceInDays(dueDateObj, startOfDay(new Date()));
};

/**
 * Format relative due date for display
 * @param {Date|string} dueDate - Due date
 * @returns {string} Relative due date string
 * 
 * @example
 * formatRelativeDueDate(tomorrow) // 'Due tomorrow'
 * formatRelativeDueDate(yesterday) // 'Overdue by 1 day'
 * formatRelativeDueDate(nextWeek) // 'Due in 7 days'
 */
export const formatRelativeDueDate = (dueDate) => {
  if (!dueDate) return '';
  const dueDateObj = typeof dueDate === 'string' ? parseISO(dueDate) : new Date(dueDate);
  if (!isValid(dueDateObj)) return '';
  
  const days = getDaysUntilDue(dueDateObj);
  
  if (days === 0) return 'Due today';
  if (days === 1) return 'Due tomorrow';
  if (days === -1) return 'Overdue by 1 day';
  if (days > 1) return `Due in ${days} days`;
  if (days < -1) return `Overdue by ${Math.abs(days)} days`;
  
  return '';
};

export {
  format,
  parseISO,
  parse,
  isValid,
  addDays,
  addMonths,
  addYears,
  subDays,
  subMonths,
  subYears,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInSeconds,
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  isBefore,
  isAfter,
  isEqual,
  isSameDay,
  isSameMonth,
  isSameYear,
  isToday,
  isYesterday,
  isTomorrow,
  isWeekend,
  getDay,
  getMonth,
  getYear,
  setDay,
  setMonth,
  setYear,
};
